import React, { useState, useEffect } from 'react';
import { Container, Table, Card, Row, Col, Badge, Button, Modal, Form, Spinner, Pagination, InputGroup, FormControl, DropdownButton, Dropdown, Tab, Tabs } from 'react-bootstrap';
import Navbarpro from './sidebar';
import { api } from '../../../config';

function Orders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            let data;
            try {
                const res = await api.get('/admin/orders');
                data = res.data.orders || [];
            } catch {
                const res = await api.get('/dashboard/recent');
                data = res.data || [];
            }
            setOrders(data);
        } catch (err) {
            console.error('Error:', err);
            setError('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    // Filter orders
    const filteredOrders = orders.filter(order => {
        const matchesSearch = !searchTerm ||
            order._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customerName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = !statusFilter || order.orderStatus === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

    // Stats
    const stats = {
        total: orders.length,
        pending: orders.filter(o => !o.orderStatus || o.orderStatus === 'Pending').length,
        processing: orders.filter(o => o.orderStatus === 'Processing').length,
        shipped: orders.filter(o => o.orderStatus === 'Shipped').length,
        delivered: orders.filter(o => o.orderStatus === 'Delivered').length,
        cancelled: orders.filter(o => o.orderStatus === 'Cancelled').length,
        revenue: orders.reduce((sum, o) => sum + (o.totalPrice || 0), 0)
    };

    const handleUpdateStatus = async () => {
        if (!selectedOrder || !newStatus) return;
        setActionLoading(true);
        try {
            await api.put(`/admin/orders/${selectedOrder._id}`, { status: newStatus });
            setSuccess('Order status updated');
            setShowUpdateModal(false);
            fetchOrders();
        } catch (err) {
            setError('Failed to update order status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelOrder = async (orderId) => {
        if (!window.confirm('Are you sure you want to cancel this order?')) return;
        try {
            await api.put(`/admin/orders/${orderId}/cancel`, { reason: 'Cancelled by admin' });
            setSuccess('Order cancelled');
            fetchOrders();
        } catch (err) {
            setError('Failed to cancel order');
        }
    };

    const getStatusConfig = (status) => {
        const configs = {
            Pending: { bg: 'warning', icon: 'hourglass-split', color: '#FFD600' },
            Processing: { bg: 'primary', icon: 'gear', color: '#FF7600' },
            Shipped: { bg: 'info', icon: 'truck', color: '#00D4FF' },
            Delivered: { bg: 'success', icon: 'check-circle', color: '#00C853' },
            Cancelled: { bg: 'danger', icon: 'x-circle', color: '#FF5252' }
        };
        return configs[status] || configs.Pending;
    };

    if (loading) {
        return (
            <div className="loading-state">
                <Navbarpro />
                <div className="text-center" style={{ marginTop: '20vh' }}>
                    <Spinner animation="border" />
                    <p className="mt-3">Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Navbarpro />

            <Container fluid className="py-4 px-4">
                {/* Page Header */}
                <div className="page-header d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="mb-1">
                            <i className="bi bi-bag me-2" style={{ color: 'var(--accent-purple)' }}></i>
                            Order Management
                        </h4>
                        <p className="text-secondary mb-0">View and manage all orders</p>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={fetchOrders}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                    </Button>
                </div>

                {/* Alerts */}
                {error && <div className="alert alert-danger">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {/* Stats */}
                <Row className="g-3 mb-4">
                    <Col lg={2} md={4} sm={6}>
                        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setStatusFilter('')}>
                            <div className="d-flex flex-column">
                                <div className="value">{stats.total}</div>
                                <div className="label">Total Orders</div>
                            </div>
                        </div>
                    </Col>
                    {['Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'].map(status => {
                        const config = getStatusConfig(status);
                        return (
                            <Col lg={2} md={4} sm={6} key={status}>
                                <div
                                    className="stat-card"
                                    style={{ cursor: 'pointer', borderTopColor: config.color }}
                                    onClick={() => setStatusFilter(status)}
                                >
                                    <div className="d-flex flex-column">
                                        <div className="value" style={{ color: config.color }}>{stats[status.toLowerCase()]}</div>
                                        <div className="label">{status}</div>
                                    </div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>

                {/* Revenue Card */}
                <Card className="mb-4" style={{ background: 'var(--gradient-primary)' }}>
                    <Card.Body className="d-flex align-items-center justify-content-between">
                        <div>
                            <h6 className="text-white-50 mb-1">Total Revenue</h6>
                            <h2 className="text-white mb-0">₹{stats.revenue.toLocaleString()}</h2>
                        </div>
                        <i className="bi bi-currency-rupee fs-1 text-white-50"></i>
                    </Card.Body>
                </Card>

                {/* Filters */}
                <Card className="mb-4">
                    <Card.Body>
                        <Row className="align-items-center g-3">
                            <Col md={5}>
                                <InputGroup>
                                    <FormControl
                                        placeholder="Search by order ID or customer..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <Button variant="outline-secondary">
                                        <i className="bi bi-search"></i>
                                    </Button>
                                </InputGroup>
                            </Col>
                            <Col md={3}>
                                <DropdownButton
                                    title={statusFilter || "All Statuses"}
                                    variant="outline-secondary"
                                    onSelect={(e) => setStatusFilter(e)}
                                >
                                    <Dropdown.Item eventKey="">All Statuses</Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item eventKey="Pending">Pending</Dropdown.Item>
                                    <Dropdown.Item eventKey="Processing">Processing</Dropdown.Item>
                                    <Dropdown.Item eventKey="Shipped">Shipped</Dropdown.Item>
                                    <Dropdown.Item eventKey="Delivered">Delivered</Dropdown.Item>
                                    <Dropdown.Item eventKey="Cancelled">Cancelled</Dropdown.Item>
                                </DropdownButton>
                            </Col>
                            <Col md={4} className="text-end">
                                <span className="text-secondary">{filteredOrders.length} orders</span>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Orders Table */}
                {filteredOrders.length === 0 ? (
                    <div className="empty-state">
                        <i className="bi bi-inbox"></i>
                        <h5>No orders found</h5>
                        <p>Try adjusting your search or filters.</p>
                    </div>
                ) : (
                    <div className="data-table-wrapper">
                        <Table hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Customer</th>
                                    <th>Items</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((order) => {
                                    const statusConfig = getStatusConfig(order.orderStatus);
                                    return (
                                        <tr key={order._id}>
                                            <td>
                                                <code className="text-primary">#{order._id?.slice(-8).toUpperCase()}</code>
                                            </td>
                                            <td>
                                                <div className="fw-medium">{order.customerName || 'Unknown'}</div>
                                                {order.customerEmail && (
                                                    <small className="text-secondary">{order.customerEmail}</small>
                                                )}
                                            </td>
                                            <td>{order.products?.length || order.items?.length || 1} item(s)</td>
                                            <td>
                                                <span className="fw-bold" style={{ color: 'var(--accent-green)' }}>
                                                    ₹{order.totalPrice || 0}
                                                </span>
                                            </td>
                                            <td>
                                                <Badge bg={statusConfig.bg} className="d-inline-flex align-items-center gap-1">
                                                    <i className={`bi bi-${statusConfig.icon}`}></i>
                                                    {order.orderStatus || 'Pending'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <small>
                                                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                                                </small>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => { setSelectedOrder(order); setShowModal(true); }}
                                                        title="View Details"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </Button>
                                                    <Button
                                                        variant="outline-warning"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedOrder(order);
                                                            setNewStatus(order.orderStatus || 'Pending');
                                                            setShowUpdateModal(true);
                                                        }}
                                                        title="Update Status"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </Button>
                                                    {order.orderStatus !== 'Cancelled' && order.orderStatus !== 'Delivered' && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleCancelOrder(order._id)}
                                                            title="Cancel Order"
                                                        >
                                                            <i className="bi bi-x-lg"></i>
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <span className="text-secondary">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredOrders.length)} of {filteredOrders.length}
                        </span>
                        <Pagination>
                            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                                if (pageNum > totalPages) return null;
                                return (
                                    <Pagination.Item key={pageNum} active={currentPage === pageNum} onClick={() => setCurrentPage(pageNum)}>
                                        {pageNum}
                                    </Pagination.Item>
                                );
                            })}
                            <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Container>

            {/* Order Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>
                        <i className="bi bi-bag me-2"></i>
                        Order Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedOrder && (
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6 className="text-secondary">Order ID</h6>
                                    <code className="fs-5">#{selectedOrder._id?.slice(-8).toUpperCase()}</code>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Status</h6>
                                    <Badge bg={getStatusConfig(selectedOrder.orderStatus).bg} className="fs-6">
                                        {selectedOrder.orderStatus || 'Pending'}
                                    </Badge>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6 className="text-secondary">Customer</h6>
                                    <p className="mb-0">{selectedOrder.customerName || 'Unknown'}</p>
                                    <small className="text-muted">{selectedOrder.customerEmail}</small>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Total Amount</h6>
                                    <p className="mb-0 fs-4" style={{ color: 'var(--accent-green)' }}>
                                        ₹{selectedOrder.totalPrice || 0}
                                    </p>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={12}>
                                    <h6 className="text-secondary">Shipping Address</h6>
                                    <p className="mb-0">
                                        <i className="bi bi-geo-alt me-2"></i>
                                        {selectedOrder.shippingAddress?.fullAddress || 'N/A'}
                                    </p>
                                </Col>
                            </Row>
                            {selectedOrder.products && selectedOrder.products.length > 0 && (
                                <div>
                                    <h6 className="text-secondary mb-3">Order Items</h6>
                                    <Table size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Qty</th>
                                                <th>Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedOrder.products.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.name || item.productName || 'Product'}</td>
                                                    <td>{item.quantity || 1}</td>
                                                    <td>₹{item.price || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Update Status Modal */}
            <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>Update Order Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>New Status</Form.Label>
                        <Form.Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                            <option value="Pending">Pending</option>
                            <option value="Processing">Processing</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Delivered">Delivered</option>
                            <option value="Cancelled">Cancelled</option>
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleUpdateStatus} disabled={actionLoading}>
                        {actionLoading ? <Spinner size="sm" /> : 'Update'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default Orders;
